import React, { FC, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { Box, Button, Typography } from "@mui/material";
import PageWrapper from "@components/Home/PageWrapper";
import { useCreateSessionMutation, useGetProgressQuery } from "@store/gameApi";
import type { GameSessionCreateResponse } from "@models/game";
import { RootState } from "@store/store";

export interface GuidedPreScreenProps {
  basePath?: string;
}

const GuidedPreScreen: FC<GuidedPreScreenProps> = ({ basePath = "" }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const selectedPatientId = useSelector(
    (state: RootState) => state.home.selectedPatientId,
  );
  const [createSession, { isLoading }] = useCreateSessionMutation();
  const { data: progress } = useGetProgressQuery(
    { patientId: selectedPatientId! },
    { skip: !selectedPatientId },
  );
  const prefix = basePath ? `/${basePath}` : "";

  const [error, setError] = useState<string | null>(null);

  const handleStart = () => {
    if (!selectedPatientId) return;
    setError(null);
    createSession({ sessionType: "guided", patientId: selectedPatientId })
      .unwrap()
      .then((res: GameSessionCreateResponse) => {
        navigate(`${prefix}/games/guided/play`, {
          state: {
            sessionId: res.sessionId,
            sessionType: res.sessionType,
            games: res.games,
          },
        });
      })
      .catch((err: { status?: number }) => {
        setError(
          err?.status === 403
            ? t("games.errors.onlyPatientCanPlay")
            : t("errors.unknown"),
        );
      });
  };

  return (
    <PageWrapper>
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        py={4}
        px={2}
        minHeight="60vh"
      >
        <Typography variant="h4" fontWeight={700} gutterBottom textAlign="center">
          {t("games.guided.preTitle")}
        </Typography>
        {progress?.streakDay != null && progress.streakDay > 0 && (
          <Typography variant="body1" color="primary.main" sx={{ mb: 1 }}>
            {t("games.cta.streak", { count: progress.streakDay })}
          </Typography>
        )}
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          {t("games.guided.preSubtitle")}
        </Typography>
        {error && (
          <Typography color="error" sx={{ mb: 2 }}>
            {error}
          </Typography>
        )}
        <Button
          variant="contained"
          size="large"
          onClick={handleStart}
          disabled={isLoading || !selectedPatientId}
          sx={{ minHeight: 72, minWidth: 240 }}
        >
          {isLoading ? "..." : t("games.guided.startSession")}
        </Button>
      </Box>
    </PageWrapper>
  );
};

export default GuidedPreScreen;
